/**
 * MaritalAttestationCard — each party confirms THEIR OWN marital status for a
 * deal in a marital-property state (Wisconsin). A married party names their
 * spouse and gives the spouse's email and mobile phone; DocuSign Phone
 * Authentication texts or calls that number before an emailed spouse signs.
 *
 * The backend blocks sending the agreement until both parties have confirmed
 * (send-readiness reasons). This card renders nothing when the attestation
 * response says `required: false`.
 *
 * Props:
 *   offerId        – the accepted offer
 *   fallbackRole   – 'buyer' | 'seller'; used only if the response omits my_role
 *   onStatusChange – ({ required, complete }) whenever the known status changes
 *   onSaved        – (row) after the caller's attestation is saved
 *
 * Legal copy carries a "not legal advice" disclaimer (reviewed wording from
 * the Send dialog's marital-property notice).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getMaritalAttestations,
  saveMaritalAttestation,
  MARITAL_STATUS,
} from '../../agents/maritalAttestationAgent';
import styles from './MaritalAttestationCard.module.css';

export const COPY = {
  heading: 'Your marital status for this deal',
  intro:
    "Wisconsin law may require a seller's spouse to sign the sale of the couple's home, even if the spouse isn't on the title. We also add a married buyer's spouse as a signer. Confirm your status so every required signer is on the agreement.",
  disclaimer:
    'General information, not legal advice. Consult a licensed Wisconsin real estate attorney about your situation.',
  loading: 'Checking marital status…',
  loadError: "We couldn't load marital status for this deal.",
  retry: 'Retry',
  statusLegend: 'Marital status',
  statusHelper:
    "Choose Married if you're married, including if you're separated or your divorce isn't final. Choose Single if you've never married, your divorce is final, or you're widowed.",
  single: 'Single',
  married: 'Married',
  spouseName: 'Spouse full legal name',
  spouseEmail: 'Spouse email',
  spousePhone: 'Spouse mobile phone',
  spousePhoneHelper:
    "DocuSign will text or call this number so your spouse can confirm it's them before signing. Enter your spouse's own number, not yours. Signing for your spouse without their permission may make the agreement unenforceable.",
  certify: 'By confirming, you state this information is true and complete.',
  certifySpouseContact: "By entering your spouse's details, you also confirm they agree to be contacted for signing.",
  submit: 'Confirm marital status',
  saveChanges: 'Save changes',
  saving: 'Saving…',
  cancel: 'Cancel',
  edit: 'Edit',
  saveErrorFallback: "We couldn't save your marital status. Please try again.",
  youConfirmedSingle: 'You confirmed you are single.',
  youConfirmedMarried: 'You confirmed you are married.',
  otherConfirmed: (role) => `The ${role} has confirmed their marital status`,
  otherWaiting: (role) => `Waiting for the ${role} to confirm their marital status`,
  errors: {
    status: 'Choose Single or Married.',
    spouseName: "Enter your spouse's full legal name.",
    spouseEmail: "Enter a valid email address for your spouse.",
    spousePhone: "Enter your spouse's mobile number, including area code.",
  },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM = {
  marital_status: '',
  spouse_name: '',
  spouse_email: '',
  spouse_phone: '',
};

const phoneDigits = (value) => String(value || '').replace(/\D/g, '');

/** US mobile: 10 digits, or 11 with a leading country code 1. */
const isValidPhone = (value) => {
  const digits = phoneDigits(value);
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

export const validateAttestationForm = (form) => {
  const errors = {};
  if (form.marital_status !== MARITAL_STATUS.SINGLE && form.marital_status !== MARITAL_STATUS.MARRIED) {
    errors.marital_status = COPY.errors.status;
    return errors;
  }
  if (form.marital_status === MARITAL_STATUS.MARRIED) {
    if (!form.spouse_name.trim()) errors.spouse_name = COPY.errors.spouseName;
    if (!EMAIL_PATTERN.test(form.spouse_email.trim())) errors.spouse_email = COPY.errors.spouseEmail;
    if (!isValidPhone(form.spouse_phone)) errors.spouse_phone = COPY.errors.spousePhone;
  }
  return errors;
};

const formFromRow = (row) => (row
  ? {
    marital_status: row.marital_status || '',
    spouse_name: row.spouse_name || '',
    spouse_email: row.spouse_email || '',
    spouse_phone: row.spouse_phone || '',
  }
  : EMPTY_FORM);

const otherRoleOf = (role) => (role === 'seller' ? 'buyer' : 'seller');

const MaritalAttestationCard = ({ offerId, fallbackRole, onStatusChange, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [locked, setLocked] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Keep the latest callbacks without re-running effects when a parent
  // passes an inline function.
  const onStatusChangeRef = useRef(onStatusChange);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
    onSavedRef.current = onSaved;
  }, [onStatusChange, onSaved]);

  const load = useCallback(async () => {
    if (offerId == null) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await getMaritalAttestations(offerId);
      setData(payload || null);
    } catch (err) {
      setError(err?.message || COPY.loadError);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    let active = true;
    if (offerId == null) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    getMaritalAttestations(offerId)
      .then((payload) => { if (active) setData(payload || null); })
      .catch((err) => { if (active) setError(err?.message || COPY.loadError); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [offerId]);

  const myRole = data?.my_role || fallbackRole || 'buyer';
  const otherRole = otherRoleOf(myRole);
  const required = data ? !!data.required : null;

  const { mine, other } = useMemo(() => {
    const rows = Array.isArray(data?.attestations) ? data.attestations : [];
    return {
      mine: rows.find((r) => r.party_role === myRole) || null,
      other: rows.find((r) => r.party_role === otherRole) || null,
    };
  }, [data, myRole, otherRole]);

  const complete = required === false ? true : !!(mine && other);

  useEffect(() => {
    if (required === null) return;
    onStatusChangeRef.current?.({ required, complete });
  }, [required, complete]);

  const showForm = !mine || editing;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const startEdit = () => {
    setForm(formFromRow(mine));
    setFieldErrors({});
    setSaveError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setFieldErrors({});
    setSaveError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const errors = validateAttestationForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const body = { marital_status: form.marital_status };
    if (form.marital_status === MARITAL_STATUS.MARRIED) {
      body.spouse_name = form.spouse_name.trim();
      body.spouse_email = form.spouse_email.trim();
      body.spouse_phone = form.spouse_phone.trim();
    }

    setSaving(true);
    setSaveError(null);
    try {
      const row = await saveMaritalAttestation(offerId, body);
      const savedRow = { party_role: myRole, ...row };
      setData((prev) => {
        const rows = Array.isArray(prev?.attestations) ? prev.attestations : [];
        return {
          ...(prev || {}),
          attestations: [...rows.filter((r) => r.party_role !== myRole), savedRow],
        };
      });
      setEditing(false);
      onSavedRef.current?.(savedRow);
    } catch (err) {
      const status = err?.status ?? err?.statusCode;
      if (status === 409) {
        // The agreement was already sent — the status can't change any more.
        setLocked(true);
        setEditing(false);
      }
      setSaveError(err?.message || COPY.saveErrorFallback);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <section className={styles.card} data-testid="marital-attestation-card" aria-busy="true">
        <p className={styles.muted} role="status">{COPY.loading}</p>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className={styles.card} data-testid="marital-attestation-card">
        <div className={styles.error} role="alert">
          <span>{error}</span>
          <button type="button" className={styles.linkButton} onClick={load}>
            {COPY.retry}
          </button>
        </div>
      </section>
    );
  }

  if (!required) return null;

  const idBase = `marital-${offerId}`;
  const isMarried = form.marital_status === MARITAL_STATUS.MARRIED;

  return (
    <section
      className={styles.card}
      aria-labelledby={`${idBase}-heading`}
      data-testid="marital-attestation-card"
    >
      <h3 id={`${idBase}-heading`} className={styles.heading}>{COPY.heading}</h3>
      <p className={styles.intro}>{COPY.intro}</p>

      {showForm ? (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <fieldset
            className={styles.fieldset}
            aria-describedby={fieldErrors.marital_status ? `${idBase}-status-error` : undefined}
          >
            <legend className={styles.legend}>{COPY.statusLegend}</legend>
            <p id={`${idBase}-status-helper`} className={styles.helper}>{COPY.statusHelper}</p>
            <div className={styles.radioRow}>
              {[
                { value: MARITAL_STATUS.SINGLE, label: COPY.single },
                { value: MARITAL_STATUS.MARRIED, label: COPY.married },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={
                    `${styles.radioOption} ${form.marital_status === opt.value ? styles.radioOptionSelected : ''}`
                  }
                >
                  <input
                    type="radio"
                    name={`${idBase}-status`}
                    value={opt.value}
                    checked={form.marital_status === opt.value}
                    onChange={() => updateField('marital_status', opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            {fieldErrors.marital_status && (
              <p id={`${idBase}-status-error`} className={styles.fieldError}>
                {fieldErrors.marital_status}
              </p>
            )}
          </fieldset>

          {isMarried && (
            <div className={styles.spouseFields}>
              <div className={styles.field}>
                <label htmlFor={`${idBase}-spouse-name`} className={styles.label}>
                  {COPY.spouseName}
                </label>
                <input
                  id={`${idBase}-spouse-name`}
                  type="text"
                  className={styles.input}
                  autoComplete="off"
                  value={form.spouse_name}
                  onChange={(e) => updateField('spouse_name', e.target.value)}
                  aria-invalid={fieldErrors.spouse_name ? true : undefined}
                  aria-describedby={fieldErrors.spouse_name ? `${idBase}-spouse-name-error` : undefined}
                />
                {fieldErrors.spouse_name && (
                  <p id={`${idBase}-spouse-name-error`} className={styles.fieldError}>
                    {fieldErrors.spouse_name}
                  </p>
                )}
              </div>

              <div className={styles.field}>
                <label htmlFor={`${idBase}-spouse-email`} className={styles.label}>
                  {COPY.spouseEmail}
                </label>
                <input
                  id={`${idBase}-spouse-email`}
                  type="email"
                  className={styles.input}
                  autoComplete="off"
                  value={form.spouse_email}
                  onChange={(e) => updateField('spouse_email', e.target.value)}
                  aria-invalid={fieldErrors.spouse_email ? true : undefined}
                  aria-describedby={fieldErrors.spouse_email ? `${idBase}-spouse-email-error` : undefined}
                />
                {fieldErrors.spouse_email && (
                  <p id={`${idBase}-spouse-email-error`} className={styles.fieldError}>
                    {fieldErrors.spouse_email}
                  </p>
                )}
              </div>

              <div className={styles.field}>
                <label htmlFor={`${idBase}-spouse-phone`} className={styles.label}>
                  {COPY.spousePhone}
                </label>
                <input
                  id={`${idBase}-spouse-phone`}
                  type="tel"
                  inputMode="tel"
                  className={styles.input}
                  autoComplete="off"
                  placeholder="(555) 555-0123"
                  value={form.spouse_phone}
                  onChange={(e) => updateField('spouse_phone', e.target.value)}
                  aria-invalid={fieldErrors.spouse_phone ? true : undefined}
                  aria-describedby={
                    `${idBase}-spouse-phone-helper`
                    + (fieldErrors.spouse_phone ? ` ${idBase}-spouse-phone-error` : '')
                  }
                />
                <p id={`${idBase}-spouse-phone-helper`} className={styles.helper}>
                  {COPY.spousePhoneHelper}
                </p>
                {fieldErrors.spouse_phone && (
                  <p id={`${idBase}-spouse-phone-error`} className={styles.fieldError}>
                    {fieldErrors.spouse_phone}
                  </p>
                )}
              </div>
            </div>
          )}

          {saveError && (
            <div className={styles.error} role="alert">{saveError}</div>
          )}

          <p className={styles.helper} data-testid="marital-attestation-certify">
            {COPY.certify}
            {isMarried && ` ${COPY.certifySpouseContact}`}
          </p>

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={saving}
              aria-busy={saving || undefined}
            >
              {saving ? COPY.saving : (mine ? COPY.saveChanges : COPY.submit)}
            </button>
            {mine && (
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={cancelEdit}
                disabled={saving}
              >
                {COPY.cancel}
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className={styles.summary} data-testid="marital-attestation-summary">
          <span className={styles.checkIcon} aria-hidden="true">✓</span>
          <div className={styles.summaryBody}>
            <p className={styles.summaryLine}>
              {mine.marital_status === MARITAL_STATUS.MARRIED
                ? COPY.youConfirmedMarried
                : COPY.youConfirmedSingle}
            </p>
            {mine.marital_status === MARITAL_STATUS.MARRIED && (
              <dl className={styles.details}>
                <dt>Spouse</dt>
                <dd>{mine.spouse_name || '—'}</dd>
                <dt>Email</dt>
                <dd>{mine.spouse_email || '—'}</dd>
                <dt>Mobile</dt>
                <dd>{mine.spouse_phone_last4 ? `•••${mine.spouse_phone_last4}` : '—'}</dd>
              </dl>
            )}
          </div>
          {!locked && (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={startEdit}
              aria-label="Edit your marital status"
            >
              {COPY.edit}
            </button>
          )}
        </div>
      )}

      {!showForm && saveError && (
        <div className={styles.error} role="alert">{saveError}</div>
      )}

      <p
        className={`${styles.otherParty} ${other ? styles.otherPartyDone : ''}`}
        data-testid="marital-attestation-other"
      >
        <span aria-hidden="true">{other ? '✓' : '⏳'}</span>{' '}
        {other ? COPY.otherConfirmed(otherRole) : COPY.otherWaiting(otherRole)}
      </p>

      <p className={styles.disclaimer}>{COPY.disclaimer}</p>
    </section>
  );
};

export default MaritalAttestationCard;
